import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { ChartLineUp, Eye, UsersThree, UserPlus, DeviceMobile, Desktop } from "@phosphor-icons/react";

const num = (v) => Number(v || 0).toLocaleString("it-IT");

function KpiCard({ icon: Icon, label, value, sub, testid }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-card p-5" data-testid={testid}>
            <div className="flex items-center gap-2 text-gray-500 mb-2">
                <Icon size={16} weight="fill" />
                <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
            </div>
            <div className="font-display-black text-2xl tabular-nums">{value}</div>
            {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
    );
}

const PAGE_LABELS = {
    "/": "Landing page",
    "/login": "Login",
    "/register": "Registrazione",
    "/dashboard": "Dashboard",
    "/radar": "Radar",
    "/piano": "Piano / Upgrade",
};

export default function Traffic() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        api.get("/admin/traffic/summary", { params: { days: 30 } })
            .then(({ data }) => setData(data))
            .catch(() => setErr(true));
    }, []);

    if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

    const daily = (data?.daily || []).map((d) => ({
        ...d,
        label: d.date.slice(8) + "/" + d.date.slice(5, 7),
    }));
    const devTotal = data ? (data.devices.mobile || 0) + (data.devices.desktop || 0) : 0;
    const mobilePct = devTotal ? Math.round(((data.devices.mobile || 0) / devTotal) * 100) : 0;

    return (
        <Layout>
            <div className="space-y-6" data-testid="admin-traffic">
                <div>
                    <h1 className="font-display-black text-3xl flex items-center gap-2">
                        <ChartLineUp size={26} weight="fill" /> Traffico
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Visite e pagine viste registrate direttamente nel database — nessun servizio esterno,
                        nessun cookie di profilazione. Le visite dell'admin e i bot sono esclusi.
                    </p>
                </div>

                {err && (
                    <p className="text-sm text-rose-600" data-testid="traffic-error">
                        Impossibile caricare i dati di traffico.
                    </p>
                )}

                {data && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="traffic-kpi">
                            <KpiCard icon={UsersThree} label="Visite oggi"
                                     value={num(data.today.sessions)}
                                     sub={`${num(data.today.pageviews)} pagine viste`}
                                     testid="traffic-kpi-today" />
                            <KpiCard icon={Eye} label="Visite 30 giorni"
                                     value={num(data.window.sessions)}
                                     sub={`${num(data.window.pageviews)} pagine viste`}
                                     testid="traffic-kpi-window" />
                            <KpiCard icon={UserPlus} label="Registrazioni 30 giorni"
                                     value={num(data.window.registrations)}
                                     sub={`${num(data.today.registrations)} oggi`}
                                     testid="traffic-kpi-reg" />
                            <KpiCard icon={mobilePct >= 50 ? DeviceMobile : Desktop} label="Da mobile"
                                     value={`${mobilePct}%`}
                                     sub={`${num(data.devices.mobile || 0)} mobile · ${num(data.devices.desktop || 0)} desktop`}
                                     testid="traffic-kpi-device" />
                        </div>

                        <div className="bg-white border border-gray-200 rounded-xl shadow-card p-5" data-testid="traffic-daily-chart">
                            <h3 className="font-display-black text-base mb-4">Andamento giornaliero — ultimi 30 giorni</h3>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.15)" />
                                        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={2} />
                                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                        <Tooltip
                                            formatter={(v, name) => [num(v), { pageviews: "Pagine viste", sessions: "Visite", registrations: "Registrazioni" }[name] || name]}
                                            labelFormatter={(l) => `Giorno ${l}`}
                                            contentStyle={{ background: "#141C33", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                                        />
                                        <Legend formatter={(v) => ({ pageviews: "Pagine viste", sessions: "Visite", registrations: "Registrazioni" }[v] || v)}
                                                wrapperStyle={{ fontSize: 12 }} />
                                        <Bar dataKey="pageviews" fill="#3B66F5" radius={[3, 3, 0, 0]} />
                                        <Bar dataKey="sessions" fill="#22D3EE" radius={[3, 3, 0, 0]} />
                                        <Bar dataKey="registrations" fill="#10B981" radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="grid lg:grid-cols-2 gap-4">
                            <div className="bg-white border border-gray-200 rounded-xl shadow-card overflow-x-auto" data-testid="traffic-top-pages">
                                <div className="p-5 pb-0">
                                    <h3 className="font-display-black text-base">Pagine più viste (30 giorni)</h3>
                                </div>
                                <table className="w-full text-sm mt-3">
                                    <thead>
                                        <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                                            <th className="px-5 py-2">Pagina</th>
                                            <th className="px-3 py-2">Viste</th>
                                            <th className="px-5 py-2">%</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.top_pages.map((p) => (
                                            <tr key={p.path} className="border-b border-gray-50">
                                                <td className="px-5 py-2.5">
                                                    <span className="font-medium">{PAGE_LABELS[p.path] || p.path}</span>
                                                    {PAGE_LABELS[p.path] && <span className="text-xs text-gray-400 ml-2">{p.path}</span>}
                                                </td>
                                                <td className="px-3 py-2.5 tabular-nums font-semibold">{num(p.views)}</td>
                                                <td className="px-5 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-1.5 w-24 bg-gray-100 rounded-full overflow-hidden">
                                                            <div className="h-full bg-[#3B66F5] rounded-full"
                                                                 style={{ width: `${data.window.pageviews ? Math.round((p.views / data.window.pageviews) * 100) : 0}%` }} />
                                                        </div>
                                                        <span className="text-xs tabular-nums text-gray-500">
                                                            {data.window.pageviews ? Math.round((p.views / data.window.pageviews) * 100) : 0}%
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {data.top_pages.length === 0 && (
                                            <tr><td colSpan={3} className="px-5 py-6 text-center text-sm text-gray-500" data-testid="traffic-pages-empty">
                                                Nessuna visita registrata ancora — i dati compariranno dalle prossime visite al sito.
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-xl shadow-card overflow-x-auto" data-testid="traffic-referrers">
                                <div className="p-5 pb-0">
                                    <h3 className="font-display-black text-base">Provenienza (30 giorni)</h3>
                                </div>
                                <table className="w-full text-sm mt-3">
                                    <thead>
                                        <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                                            <th className="px-5 py-2">Sorgente</th>
                                            <th className="px-3 py-2">Viste</th>
                                            <th className="px-5 py-2">%</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.referrers.map((r) => (
                                            <tr key={r.source} className="border-b border-gray-50">
                                                <td className="px-5 py-2.5 font-medium">{r.source === "diretto" ? "Traffico diretto" : r.source}</td>
                                                <td className="px-3 py-2.5 tabular-nums font-semibold">{num(r.views)}</td>
                                                <td className="px-5 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-1.5 w-24 bg-gray-100 rounded-full overflow-hidden">
                                                            <div className="h-full bg-[#22D3EE] rounded-full"
                                                                 style={{ width: `${data.window.pageviews ? Math.round((r.views / data.window.pageviews) * 100) : 0}%` }} />
                                                        </div>
                                                        <span className="text-xs tabular-nums text-gray-500">
                                                            {data.window.pageviews ? Math.round((r.views / data.window.pageviews) * 100) : 0}%
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {data.referrers.length === 0 && (
                                            <tr><td colSpan={3} className="px-5 py-6 text-center text-sm text-gray-500">
                                                Nessuna provenienza registrata ancora.
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <p className="text-[11px] text-gray-400">
                            "Visite" = sessioni di navigazione uniche (ID anonimo per sessione, nessun dato personale salvato).
                            Tracciamento first-party conforme alla Cookie Policy: nessun cookie di terze parti.
                        </p>
                    </>
                )}
            </div>
        </Layout>
    );
}
